import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '@dia/i18n';
import { LockIcon } from './shellIcons';

/**
 * One tile on the guest home (docs/GUEST-REDESIGN.md, section 3).
 *
 * A launcher, not a card of reading: an icon in a soft Lagoon square, the
 * name, and at most one live line plus one fixed line. The home grid is the
 * one place in the guest shell where a grid of boxes is allowed (CLAUDE.md
 * Section 9). A wide tile spans both columns.
 *
 * A tile with no `to` is a note, not a button: same shape, no press effect, no
 * link. The sunshine count is one, because it has nowhere to go and a tile
 * that looks tappable and does nothing is worse than a quiet card.
 *
 * A locked tile stays where it is, so a thumb that learned the order is never
 * surprised: it drops the link, dims the name, puts a lock on the icon, and
 * says why in one line. Color is never the only signal.
 */
export function Tile({
  to,
  icon,
  name,
  live,
  hint,
  wide = false,
  locked = false,
  lockedHint,
  className = '',
  style,
}: {
  to?: string;
  icon: ReactNode;
  name: string;
  /** The live fact, in Bone. Optional. */
  live?: string | null;
  /** The fixed line, in the caption color. */
  hint?: string | null;
  wide?: boolean;
  locked?: boolean;
  lockedHint?: string;
  className?: string;
  style?: CSSProperties;
}) {
  const { t } = useT();

  const shape = wide
    ? 'flex-row items-center gap-4 min-h-[6.75rem]'
    : 'flex-col justify-between gap-3.5 min-h-[8.75rem]';

  const body = (
    <>
      <span
        className={`relative grid h-[2.875rem] w-[2.875rem] shrink-0 place-items-center rounded-[0.95rem] ${
          locked ? 'bg-ink/5 text-inkFaint' : 'bg-accent/10 text-accent'
        }`}
      >
        {icon}
        {locked && (
          <span className="absolute -bottom-1 -right-1 grid h-5 w-5 place-items-center rounded-full border border-line bg-surface text-inkMuted">
            <LockIcon className="h-3 w-3" />
          </span>
        )}
      </span>
      <span className="flex min-w-0 flex-col">
        <span className={`font-semibold leading-tight ${locked ? 'text-inkMuted' : 'text-ink'}`}>{name}</span>
        {locked ? (
          <span className="mt-1 text-caption leading-snug text-inkFaint">
            <span className="sr-only">{t('guest.tile.locked')}. </span>
            {lockedHint}
          </span>
        ) : (
          <>
            {live && <span className="mt-1 text-caption font-medium leading-snug text-ink">{live}</span>}
            {hint && <span className="mt-1 text-caption leading-snug text-inkFaint">{hint}</span>}
          </>
        )}
      </span>
    </>
  );

  const base = `flex rounded-[1.375rem] border border-line bg-surface p-4 ${shape}`;

  return (
    <li className={`${wide ? 'col-span-full' : ''} flex ${className}`} style={style}>
      {locked ? (
        <div aria-disabled="true" className={`${base} w-full`}>
          {body}
        </div>
      ) : !to ? (
        // No border: the edge is what says "tap me" on the tiles around it.
        <div className={`${base.replace('border-line', 'border-transparent')} w-full`}>{body}</div>
      ) : (
        <Link
          to={to}
          className={`${base} w-full transition-[transform,border-color] duration-150 hover:border-accent active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100`}
        >
          {body}
        </Link>
      )}
    </li>
  );
}
